"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { useTranslations } from "next-intl"
import { Loader2, Check } from "lucide-react"
import SearchableDropdown from "@/components/ui/SearchableDropdown"
import type { Option } from "@/components/ui/SearchableDropdown"
import { CreationFlowInlinePicker } from "@/components/publications/creation-flow/inline-enum-picker"
import { shouldUseClassicPicker } from "@/lib/dropdown-constants"
import type { StepProps, CardListingDraft, CardPackagingFormat } from "@/types/listing"
import { EMPTY_SPECIAL_ATTRIBUTES } from "@/types/listing"
import {
  creationFlowBlockClass,
  creationFlowSectionLabelClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import { getCardLanguageFlag } from "@/lib/flags"

interface TaxonomyItem {
  id: string
  parentId: string | null
  type: string
  slug: string | null
  name: string
  sortOrder: number
}

function dedupeTaxonomyItems(items: TaxonomyItem[]): TaxonomyItem[] {
  const seen = new Set<string>()
  const deduped: TaxonomyItem[] = []
  for (const item of items) {
    const normalizedName = item.name.trim().replace(/\s+/g, " ").toLowerCase()
    const normalizedSlug = (item.slug ?? "").trim().toLowerCase()
    const key = `${item.type}::${normalizedName || normalizedSlug}`
    if (seen.has(key)) continue
    seen.add(key)
    deduped.push(item)
  }
  return deduped
}

const FORMAT_ICONS: Record<CardPackagingFormat, string> = {
  single: "🃏",
  slab: "🏆",
  bundle: "📦",
  booster: "🎁",
  box: "📫",
  etb: "🎲",
  deck: "🗂️",
  collection: "💎",
}

async function fetchTaxonomy(
  types: string,
  parentId?: string | null,
  q?: string,
): Promise<TaxonomyItem[]> {
  const params = new URLSearchParams({ type: types, limit: "200" })
  if (parentId) params.set("parent_id", parentId)
  if (q?.trim()) params.set("q", q.trim())
  const res = await fetch(`/api/taxonomy?${params.toString()}`)
  if (!res.ok) return []
  const json = await res.json()
  return Array.isArray(json.items) ? json.items : []
}

export function CardCategoryStep({
  data,
  updateDataAction,
  onNextAction,
  onValidationChange,
  onRegisterSave,
  hideButton,
  compact,
}: StepProps) {
  const t = useTranslations("cardCreation.category")
  const draft = data.cardDraft
  const [tcgs, setTcgs] = useState<TaxonomyItem[]>([])
  const [formats, setFormats] = useState<TaxonomyItem[]>([])
  const [languages, setLanguages] = useState<TaxonomyItem[]>([])
  const [sets, setSets] = useState<TaxonomyItem[]>([])
  const [rarities, setRarities] = useState<TaxonomyItem[]>([])

  const [selectedTcg, setSelectedTcg] = useState<string | undefined>(draft?.tcgId)
  const [selectedFormat, setSelectedFormat] = useState<CardPackagingFormat | undefined>(draft?.format)
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(draft?.languageId)
  const [selectedSet, setSelectedSet] = useState<string | undefined>(draft?.tcgSetId)
  const [selectedRarity, setSelectedRarity] = useState<string | undefined>(draft?.tcgRarityId)

  const [loading, setLoading] = useState(true)
  const [setsLoading, setSetsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchTaxonomy("tcg,card_format,card_language").then((items) => {
      if (cancelled) return
      const sorted = items.slice().sort((a, b) => a.sortOrder - b.sortOrder)
      setTcgs(dedupeTaxonomyItems(sorted.filter((i) => i.type === "tcg")))
      setFormats(dedupeTaxonomyItems(sorted.filter((i) => i.type === "card_format")))
      setLanguages(dedupeTaxonomyItems(sorted.filter((i) => i.type === "card_language")))
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!selectedTcg) {
      setSets([])
      setRarities([])
      return
    }
    let cancelled = false
    setSetsLoading(true)
    Promise.all([
      fetchTaxonomy("tcg_set", selectedTcg),
      fetchTaxonomy("tcg_rarity", selectedTcg),
    ]).then(([setItems, rarityItems]) => {
      if (cancelled) return
      setSets(dedupeTaxonomyItems(setItems.sort((a, b) => a.sortOrder - b.sortOrder)))
      setRarities(dedupeTaxonomyItems(rarityItems.sort((a, b) => a.sortOrder - b.sortOrder)))
      setSetsLoading(false)
    })
    return () => { cancelled = true }
  }, [selectedTcg])

  useEffect(() => {
    if (!selectedSet) return
    let cancelled = false
    fetchTaxonomy("tcg_rarity", selectedSet).then((items) => {
      if (cancelled) return
      if (items.length > 0) setRarities(dedupeTaxonomyItems(items.sort((a, b) => a.sortOrder - b.sortOrder)))
    })
    return () => { cancelled = true }
  }, [selectedSet])

  const needsSetAndRarity = selectedFormat === "single" || selectedFormat === "slab"
  const isValid = Boolean(
    selectedTcg &&
    selectedFormat &&
    (!needsSetAndRarity || true)
  )

  useEffect(() => {
    onValidationChange?.(isValid)
  }, [isValid, onValidationChange])

  const saveData = useCallback(() => {
    if (!selectedTcg || !selectedFormat) return
    const cardDraft: CardListingDraft = {
      ...(draft ?? {
        cardCategory: "trading_card" as const,
        cardName: "",
        specialAttributes: { ...EMPTY_SPECIAL_ATTRIBUTES },
        isGraded: false,
      }),
      cardCategory: "trading_card",
      format: selectedFormat,
      tcgId: selectedTcg,
      tcgSetId: selectedSet,
      tcgRarityId: selectedRarity,
      languageId: selectedLanguage,
    }
    updateDataAction({ cardDraft })
  }, [draft, selectedTcg, selectedFormat, selectedSet, selectedRarity, selectedLanguage, updateDataAction])

  useEffect(() => {
    onRegisterSave?.(saveData)
    return () => { onRegisterSave?.(null) }
  }, [saveData, onRegisterSave])

  const handleSetSearch = useCallback(
    (q: string) => {
      if (!selectedTcg) return
      setSetsLoading(true)
      fetchTaxonomy("tcg_set", selectedTcg, q)
        .then((items) => setSets(dedupeTaxonomyItems(items.sort((a, b) => a.sortOrder - b.sortOrder))))
        .finally(() => setSetsLoading(false))
    },
    [selectedTcg],
  )

  const handleTcgSearch = useCallback((q: string) => {
    fetchTaxonomy("tcg", undefined, q).then((items) => {
      setTcgs(dedupeTaxonomyItems(items.sort((a, b) => a.sortOrder - b.sortOrder)))
    })
  }, [])

  const handleRaritySearch = useCallback(
    (q: string) => {
      if (!selectedTcg) return
      const parentId = selectedSet ?? selectedTcg
      fetchTaxonomy("tcg_rarity", parentId, q).then((items) => {
        setRarities(dedupeTaxonomyItems(items.sort((a, b) => a.sortOrder - b.sortOrder)))
      })
    },
    [selectedTcg, selectedSet],
  )

  const useInlineRarityPicker = shouldUseClassicPicker(rarities.length)
  const useInlineLanguagePicker = shouldUseClassicPicker(languages.length)

  const tcgOptions: Option[] = useMemo(
    () => tcgs.map((t) => ({ code: t.id, name: t.name, flag: "" })),
    [tcgs]
  )
  const selectedTcgOption: Option | null = useMemo(
    () => {
      if (!selectedTcg) return null
      const t = tcgs.find((x) => x.id === selectedTcg)
      return t ? { code: t.id, name: t.name, flag: "" } : null
    },
    [selectedTcg, tcgs]
  )

  const setOptions: Option[] = useMemo(
    () => sets.map((s) => ({ code: s.id, name: s.name, flag: "" })),
    [sets]
  )
  const selectedSetOption: Option | null = useMemo(
    () => {
      if (!selectedSet) return null
      const s = sets.find((x) => x.id === selectedSet)
      return s ? { code: s.id, name: s.name, flag: "" } : null
    },
    [selectedSet, sets]
  )

  const langOptions: Option[] = useMemo(
    () =>
      languages.map((l) => ({
        code: l.id,
        name: l.name,
        flag: getCardLanguageFlag(l.slug),
      })),
    [languages]
  )
  const selectedLangOption: Option | null = useMemo(
    () => {
      if (!selectedLanguage) return null
      const l = languages.find((x) => x.id === selectedLanguage)
      return l
        ? { code: l.id, name: l.name, flag: getCardLanguageFlag(l.slug) }
        : null
    },
    [selectedLanguage, languages]
  )

  const rarityOptions: Option[] = useMemo(
    () => rarities.map((r) => ({ code: r.id, name: r.name, flag: "" })),
    [rarities]
  )
  const selectedRarityOption: Option | null = useMemo(
    () => {
      if (!selectedRarity) return null
      const r = rarities.find((x) => x.id === selectedRarity)
      return r ? { code: r.id, name: r.name, flag: "" } : null
    },
    [selectedRarity, rarities]
  )

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="mb-4 h-10 w-10 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{t("loading")}</span>
      </div>
    )
  }

  const sectionLabel = creationFlowSectionLabelClass
  const cardBase = creationFlowBlockClass
  const cardPadding = compact ? "p-3" : "p-4"

  return (
    <div className={compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"}>
      <div className={cn("rounded-2xl", cardBase, cardPadding)}>
        <p className={cn(sectionLabel, "mb-3")}>{t("gameTcg")}</p>
        <SearchableDropdown
          options={tcgOptions}
          label="name"
          selectedValue={selectedTcgOption}
          onSelect={(opt) => {
            setSelectedTcg(opt.code)
            setSelectedSet(undefined)
            setSelectedRarity(undefined)
          }}
          placeholder={t("selectTcg")}
          showFlag={false}
          searchPlaceholder={t("searchGames")}
          emptyMessage={t("noGamesFound")}
          onSearchQueryChange={handleTcgSearch}
          sheetTitle={t("selectTcg")}
        />
      </div>

      {selectedTcg && (
        <div className={cn("rounded-2xl", cardBase, cardPadding)}>
          <p className={cn(sectionLabel, "mb-3")}>{t("listingFormat")}</p>
          <div className="grid grid-cols-2 gap-2">
            {formats.map((fmt) => {
              const slug = fmt.slug as CardPackagingFormat
              const isSelected = selectedFormat === slug
              return (
                <button
                  key={fmt.id}
                  type="button"
                  onClick={() => setSelectedFormat(slug)}
                  className={cn(
                    "flex items-center gap-2.5 rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors",
                    isSelected
                      ? "bg-sheet-handle/80 text-foreground ring-1 ring-foreground/30"
                      : "bg-muted/50 text-foreground/80 hover:bg-muted/70"
                  )}
                >
                  <span className="text-lg">{FORMAT_ICONS[slug] ?? "📦"}</span>
                  <span className="truncate">{fmt.name}</span>
                  {isSelected && <Check className="ml-auto h-4 w-4 shrink-0" />}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {selectedTcg && sets.length > 0 && (
        <div className={cn("rounded-2xl", cardBase, cardPadding)}>
          <p className={cn(sectionLabel, "mb-3")}>{t("setExtension")}</p>
          <SearchableDropdown
            options={setOptions}
            label="name"
            selectedValue={selectedSetOption}
            onSelect={(opt) => setSelectedSet(opt.code)}
            placeholder={t("selectSet")}
            showFlag={false}
            searchPlaceholder={t("searchSets")}
            emptyMessage={t("noSetsFound")}
            loading={setsLoading}
            onSearchQueryChange={handleSetSearch}
            sheetTitle={t("selectSet")}
          />
        </div>
      )}

      {selectedTcg && rarities.length > 0 && (
        <div className={cn("rounded-2xl", cardBase, cardPadding)}>
          <p className={cn(sectionLabel, "mb-3")}>{t("rarity")}</p>
          {useInlineRarityPicker ? (
            <CreationFlowInlinePicker
              items={rarities.map((r) => ({ id: r.id, name: r.name }))}
              selectedId={selectedRarity}
              onSelect={setSelectedRarity}
            />
          ) : (
            <SearchableDropdown
              options={rarityOptions}
              label="name"
              selectedValue={selectedRarityOption}
              onSelect={(opt) => setSelectedRarity(opt.code)}
              placeholder={t("selectRarity")}
              showFlag={false}
              searchPlaceholder={t("search")}
              emptyMessage={t("noRaritiesFound")}
              onSearchQueryChange={handleRaritySearch}
              sheetTitle={t("selectRarity")}
            />
          )}
        </div>
      )}

      <div className={cn("rounded-2xl", cardBase, cardPadding)}>
        <p className={cn(sectionLabel, "mb-3")}>{t("language")}</p>
        {useInlineLanguagePicker ? (
          <CreationFlowInlinePicker
            items={languages.map((l) => ({
              id: l.id,
              name: l.name,
              flag: getCardLanguageFlag(l.slug),
            }))}
            selectedId={selectedLanguage}
            onSelect={setSelectedLanguage}
            showFlag
          />
        ) : (
          <SearchableDropdown
            options={langOptions}
            label="name"
            selectedValue={selectedLangOption}
            onSelect={(opt) => setSelectedLanguage(opt.code)}
            placeholder={t("selectLanguage")}
            searchPlaceholder={t("search")}
            emptyMessage={t("noLanguagesFound")}
            sheetTitle={t("selectLanguage")}
          />
        )}
      </div>
    </div>
  )
}
